'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Beer, Pizza, Cookie, Zap, Radio, Target } from 'lucide-react';
import { useUser } from '@/lib/useUser';

type SupplyType = 'drink' | 'food' | 'snack' | 'other';

const QUICK_ACTIONS = [
  { label: 'Bier', type: 'drink', icon: <Beer size={18} /> },
  { label: 'Pizza', type: 'food', icon: <Pizza size={18} /> },
  { label: 'Snack', type: 'snack', icon: <Cookie size={18} /> },
  { label: 'Energy', type: 'drink', icon: <Zap size={18} /> },
];

export default function SupplyDropCompact() {
  const [item, setItem] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [availablePlayers, setAvailablePlayers] = useState<string[]>([]);
  const { username, updateUsername } = useUser();
  const [mounted, setMounted] = useState(false);
  
  // New state for user selection flow
  const [pendingOrder, setPendingOrder] = useState<{item: string, type: SupplyType} | null>(null);

  useEffect(() => {
    setMounted(true);
    const fetchPlayers = async () => {
      try {
        const res = await fetch('/api/users');
        const data = await res.json();
        setAvailablePlayers(data);
      } catch (e) {}
    };
    fetchPlayers();
    const interval = setInterval(fetchPlayers, 5000);
    return () => clearInterval(interval);
  }, []);

  const initiateOrder = (orderItem: string, type: SupplyType) => {
    if (availablePlayers.length === 0) {
      sendFinalOrder(orderItem, type, username);
      return;
    }
    setPendingOrder({ item: orderItem, type });
  };

  const sendFinalOrder = async (orderItem: string, type: SupplyType, orderingUser: string) => {
    setIsSending(true);
    setPendingOrder(null);
    updateUsername(orderingUser);

    new Audio('/audio/order.mp3').play().catch(() => {});

    await fetch('/api/supply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        action: 'create', 
        user: orderingUser, 
        item: orderItem, 
        type 
      })
    });

    setItem('');
    setTimeout(() => setIsSending(false), 500);
  };

  // Modal Content via Portal
  const selectionModal = (
    <AnimatePresence>
      {pendingOrder && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div 
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-lg shadow-2xl"
          >
            <h3 className="text-2xl font-black text-white mb-6 text-center uppercase tracking-tight">
              Wer bestellt <span className="text-red-500">{pendingOrder.item}</span>?
            </h3>
            
            <div className="grid grid-cols-2 gap-4 mb-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
              {availablePlayers.map(player => (
                <button
                  key={player}
                  onClick={() => sendFinalOrder(pendingOrder.item, pendingOrder.type, player)}
                  className="bg-slate-800 hover:bg-red-600 hover:border-red-500 border border-slate-600 text-white py-6 rounded-xl font-bold uppercase tracking-wider text-lg transition-all transform hover:scale-[1.02] shadow-lg"
                >
                  {player}
                </button>
              ))}
            </div>
            
            <button 
              onClick={() => setPendingOrder(null)}
              className="w-full py-3 text-slate-500 hover:text-white font-medium transition-colors"
            >
              Abbrechen
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-xl relative overflow-hidden">
      
      {mounted && createPortal(selectionModal, document.body)}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-red-600/20 rounded border border-red-600/30">
            <Radio className={`text-red-500 w-4 h-4 ${isSending ? 'animate-ping' : ''}`} />
          </div>
          <h2 className="text-sm font-black text-white uppercase tracking-wider">Logistik - Bestellungen</h2>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-3">
        {QUICK_ACTIONS.map((action) => (
          <button
            key={action.label}
            onClick={() => initiateOrder(action.label, action.type as SupplyType)}
            className="flex flex-col items-center justify-center bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-500 py-2 rounded transition-all active:scale-95 group"
          >
            <span className="text-slate-300 mb-1 group-hover:scale-110 transition-transform">{action.icon}</span>
            <span className="text-[10px] font-bold text-slate-400 uppercase">{action.label}</span>
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Spezialorder..."
          value={item}
          onChange={(e) => setItem(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && initiateOrder(item, 'other')}
          className="flex-1 bg-slate-950 border border-slate-700 rounded px-3 py-1 text-xs text-white focus:border-red-500 outline-none"
        />
        <button 
          onClick={() => initiateOrder(item || 'Service', 'other')}
          className="bg-red-700 hover:bg-red-600 text-white px-3 rounded font-bold text-xs uppercase"
        >
          <Target size={14} />
        </button>
      </div>
    </div>
  );
}
